/**
 * Push notifications on the device side.
 *
 * Deliberately quiet about failure. Permission can be denied, Google Play
 * Services can be missing, a build can ship without Firebase configured — none
 * of that is a reason to show an error to someone who was doing something
 * else. What matters is that when it does work, the token reaches the server
 * and a tap lands on the right screen.
 *
 * Only runs in the native app. The browser has its own permission model and
 * its own service worker; treating them as one thing is how both end up
 * half-working.
 */
import { isNative } from './api-base';
import { api } from './api';

let started = false;
let currentToken: string | null = null;

/** Where a notification's payload asks the app to go. */
type Router = (path: string) => void;

/**
 * Asks for permission, registers with FCM, and hands the token to the server.
 *
 * Call it AFTER login: a token with no account attached is a token nobody can
 * ever send to.
 */
export async function startPush(navigate?: Router): Promise<void> {
  if (!isNative() || started) return;
  started = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    // Must match the channel_id the server sends, or Android 8+ silently
    // downgrades the notification to the default channel's importance.
    await PushNotifications.createChannel?.({
      id: 'my360',
      name: 'my360',
      description: 'Leads, publicações e avisos da sua conta',
      importance: 4,
      visibility: 1,
    }).catch(() => undefined);

    await PushNotifications.addListener('registration', (token) => {
      currentToken = token.value;
      // The real platform, not a hard-coded one: the same build runs on iOS
      // the day that target exists, and a token filed under the wrong platform
      // is a token nobody notices is wrong.
      const platform = (window as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.();
      void api
        .request('/notifications/devices', {
          method: 'POST',
          body: { token: token.value, platform: platform === 'ios' ? 'ios' : 'android' },
        })
        .catch(() => {
          // Retried next launch — registration fires on every start.
        });
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] registro falhou:', err);
    });

    // Tapping a notification: the server puts the destination in `data.url`,
    // which is an absolute URL. Only its path is used, so a payload can never
    // send the app to somewhere that isn't the app.
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const raw = action.notification.data?.url;
      if (!raw || !navigate) return;
      try {
        const path = new URL(raw, window.location.origin).pathname;
        navigate(path);
      } catch {
        /* payload malformado — ignora em vez de navegar para lugar nenhum */
      }
    });

    await PushNotifications.register();
  } catch (err) {
    console.warn('[push] indisponível:', (err as Error).message);
  }
}

/**
 * Detaches this device on logout, so the next person to use the phone doesn't
 * receive the previous account's leads.
 */
export async function stopPush(): Promise<void> {
  if (!isNative()) return;
  const token = currentToken;
  currentToken = null;
  started = false;
  if (!token) return;
  try {
    await api.request(`/notifications/devices/${encodeURIComponent(token)}`, { method: 'DELETE' });
  } catch {
    /* o servidor também poda tokens mortos sozinho */
  }
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
  } catch {
    /* noop */
  }
}
